import { ApolloError, ApolloQueryResult } from "@apollo/client";
import { CatalogErrorCode } from "../@types/generated/types";

const CATALOG_ERROR_CODES = Object.values(CatalogErrorCode);

/**
 * The catalog error code of each failed item, keyed by the path of the item
 * that owns `catalog`, e.g. `translatedChannel.speakers.3`.
 */
export const catalogErrors = (
  error: Pick<ApolloError, "graphQLErrors"> | undefined
): Record<string, CatalogErrorCode> => {
  const byPath: Record<string, CatalogErrorCode> = {};
  for (const graphQLError of error?.graphQLErrors ?? []) {
    const code = CATALOG_ERROR_CODES.find(
      code => code === graphQLError.extensions?.code
    );
    const path = graphQLError.path ?? [];
    const catalogIndex = path.indexOf("catalog");
    if (!code || catalogIndex < 0) continue;
    byPath[path.slice(0, catalogIndex).join(".")] = code;
  }
  return byPath;
};

// The network or Voices failed for a moment, so asking again can succeed.
export const isTransientCatalogFailure = ({
  error,
  errors
}: Pick<ApolloQueryResult<unknown>, "error" | "errors">): boolean =>
  !!error?.networkError ||
  !!errors?.some(
    graphQLError =>
      graphQLError.extensions?.code === CatalogErrorCode.CatalogUnavailable
  );

// One code covers a voice that is retired, deprecated or not offered in the language
export const catalogSpeakerChangeMessage = (
  error: ApolloError,
  speakerName: string | null | undefined
): string =>
  error.graphQLErrors[0]?.extensions?.code ===
  CatalogErrorCode.CatalogVoiceNotSelectable
    ? `${speakerName}'s voice can't be chosen: it may be retired, deprecated or not offered in this language. Choose another voice in the Speakers tab.`
    : catalogErrorMessage(error);

export const catalogErrorMessage = (error: ApolloError): string => {
  const code = error.graphQLErrors[0]?.extensions?.code;
  switch (code) {
    case CatalogErrorCode.CatalogVoiceNotSelectable:
      return "This voice is not available for this language. Choose another voice.";
    case CatalogErrorCode.CatalogVoiceNotFound:
      return "This voice was not found. Choose another voice.";
    case CatalogErrorCode.CatalogUnavailable:
      return "Voices catalog unavailable. Try again.";
    default:
      return error.message;
  }
};
